/**
 * Monitoreo de llamadas a las APIs externas (CMF / Coelsa).
 *
 * El backend (webadmin2 billing Lambda) lee CloudWatch:
 *   - Totales por conector  → GET /metrics/api-calls            (CloudWatch Metrics)
 *   - Desglose por endpoint → GET /metrics/api-calls/by-endpoint (Logs Insights)
 *
 * Cada invocación de los conectores = una llamada HTTP a la API externa.
 * `CucuruCoelsaWebhook` es ENTRANTE (Coelsa nos llama): direction = "inbound".
 * Los timestamps llegan en UTC; la UI los formatea a hora Argentina.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

export async function jsonOrThrow(res: Response, label: string): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `${label}: HTTP ${res.status}`);
  return data;
}

// ---------- Tipos ----------
export interface MetricPoint { t: string; v: number }   // t = ISO-8601 UTC

export interface ApiSeries {
  key:       string;   // cmf | coelsa | coelsa_in | cmf_transfers
  label:     string;
  direction: "outbound" | "inbound";
  total:     number;
  points:    MetricPoint[];
}

export interface ApiTotals {
  granularity: Granularity;
  from:        string;
  to:          string;
  series:      ApiSeries[];
}

export interface EndpointSeries {
  endpoint: string;
  total:    number;
  points:   MetricPoint[];
}

export interface ApiByEndpoint {
  service:     "cmf" | "coelsa";
  granularity: Granularity;
  from:        string;
  to:          string;
  buckets:     string[];          // ISO-8601 UTC, ordenados asc
  endpoints:   EndpointSeries[];
}

export type Granularity = "minute" | "hour" | "day";

// ---------- Llamadas ----------
export function qsFrom(o: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v != null && v !== "") qs.set(k, v);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/** Totales por conector. `from`/`to` en epoch (segundos) o ISO-8601. */
export async function getApiCallTotals(params: {
  from?: string | number;
  to?:   string | number;
  granularity: Granularity;
}): Promise<ApiTotals> {
  const url = `${API_BASE}/metrics/api-calls${qsFrom({
    from: params.from != null ? String(params.from) : undefined,
    to:   params.to   != null ? String(params.to)   : undefined,
    granularity: params.granularity,
  })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "getApiCallTotals");
}

/** Desglose por endpoint (solo cmf|coelsa). Rango máx 7 días. */
export async function getApiCallsByEndpoint(params: {
  service: "cmf" | "coelsa";
  from?: string | number;
  to?:   string | number;
  granularity: Granularity;
}): Promise<ApiByEndpoint> {
  const url = `${API_BASE}/metrics/api-calls/by-endpoint${qsFrom({
    service: params.service,
    from: params.from != null ? String(params.from) : undefined,
    to:   params.to   != null ? String(params.to)   : undefined,
    granularity: params.granularity,
  })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "getApiCallsByEndpoint");
}

// ---------- Helpers de formato (hora Argentina) ----------
const AR_TZ = "America/Argentina/Buenos_Aires";

export function fmtArTime(iso: string, granularity: Granularity): string {
  const d = new Date(iso);
  if (granularity === "day") {
    return new Intl.DateTimeFormat("es-AR", {
      timeZone: AR_TZ, day: "2-digit", month: "2-digit",
    }).format(d);
  }
  if (granularity === "hour") {
    return new Intl.DateTimeFormat("es-AR", {
      timeZone: AR_TZ, day: "2-digit", month: "2-digit", hour: "2-digit",
    }).format(d);
  }
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: AR_TZ, hour: "2-digit", minute: "2-digit",
  }).format(d);
}

export function fmtArDateTime(iso: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: AR_TZ, day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).format(new Date(iso));
}

/** `datetime-local` (hora Argentina, sin tz) → epoch en segundos. */
export function arLocalToEpoch(local: string): number {
  // local = "2026-06-26T11:30". Lo interpretamos como hora Argentina (UTC-3).
  // Buenos Aires no tiene DST hace años → offset fijo -03:00.
  return Math.floor(new Date(`${local}:00-03:00`).getTime() / 1000);
}

/** epoch (segundos) → string para `datetime-local` en hora Argentina. */
export function epochToArLocal(epoch: number): string {
  const parts = new Intl.DateTimeFormat("es-AR", {
    timeZone: AR_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(epoch * 1000));
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
}

// ---------- Monitoreo de países (WAF de los servicios públicos) ----------
// GET /metrics/paises?hours=N — agregación por país/servicio/acción de los logs
// del WAF (ALB público api.cucuru.com + CloudFront). Retención: 14 días.

export interface PaisHost { host: string; requests: number }

export interface PaisRow {
  country: string;      // ISO-3166 alpha-2 ("-" = desconocido)
  total:   number;
  allowed: number;
  blocked: number;
  hosts:   PaisHost[];  // desglose por servicio, ordenado desc
}

export interface Paises {
  hours?:    number;
  range?:    string | null;
  source?:   "live" | "aggregates";
  from:      string;
  to:        string;
  total:     number;
  allowed:   number;
  blocked:   number;
  countries: PaisRow[];
  blocked_countries?: string[];   // bloqueados por la regla del panel (webadmin2-geo-block)
  allowed_countries?: string[];   // ALLOWLIST de infra (allow-traffic-only): lo que no está acá ya se bloquea
}

export async function getPaises(hours: number): Promise<Paises> {
  const res = await authFetch(`${API_BASE}/metrics/paises?hours=${hours}`, { headers: authHeader() });
  return jsonOrThrow(res, "paises");
}

const REGION_NAMES = new Intl.DisplayNames(["es"], { type: "region" });

export function countryName(iso2: string): string {
  if (!iso2 || iso2 === "-") return "Desconocido";
  try { return REGION_NAMES.of(iso2.toUpperCase()) ?? iso2; }
  catch { return iso2; }
}

export function countryFlag(iso2: string): string {
  if (!/^[a-zA-Z]{2}$/.test(iso2)) return "🌐";
  const base = 0x1f1e6;
  const [a, b] = iso2.toUpperCase();
  return String.fromCodePoint(base + a.charCodeAt(0) - 65, base + b.charCodeAt(0) - 65);
}

// ---------- Países: rangos, bitácora, investigación y geo-block ----------
export type PaisesRange = "day" | "week" | "month" | "all";

export async function getPaisesRange(range: PaisesRange): Promise<Paises> {
  const res = await authFetch(`${API_BASE}/metrics/paises?range=${range}`, { headers: authHeader() });
  return jsonOrThrow(res, "paises");
}

/** Código HTTP que devolvimos (access logs del ALB). `borde` = lo respondió el
 *  WAF/ALB (403 del WAF, 301 http→https, host desconocido) y no llegó a la app. */
export interface RespuestaHttp { status: number; n: number; borde: boolean }
export interface InvestigacionRow {
  ip: string; host: string; uri: string; action: string; requests: number;
  /** undefined = investigación anterior a esta función; [] = sin match en el ALB. */
  respuestas?: RespuestaHttp[];
}
/** Hit del vigía (Lambda vigia, cada 10 min): un detector que disparó. */
export interface VigiaHit {
  detector: string; titulo: string; clave: string;
  ip: string | null; identidad: string | null; pais?: string | null;
  n: number; gravedad: "alta" | "media"; detalle: Record<string, unknown>;
}
export type VigiaNivel = "ruido" | "vigilar" | "actuar";
/** Acción que Claude recomienda (fase 3). "bloquear" se hace en Monitoreo › IPs; las otras acá. */
export type VigiaAccion = "ignorar" | "suprimir" | "vendor_conocido" | "vigilar" | "bloquear";
export interface VigiaVeredicto {
  nivel: VigiaNivel; resumen: string; por_que: string; proximos_pasos: string[]; modelo?: string;
  accion?: VigiaAccion; accion_por_que?: string;
}
/** Investigación automática de una IP (Lambda vigia, antes del veredicto). */
export interface VigiaInvestigacion {
  ip: string; ptr?: string | null; resumen?: string; errores?: string[]; segundos?: number;
  usuarios?: { horas: number; llamadas: number; logins: number; usuarios: { usuario: string; n: number }[];
               paths_top: { path: string; n: number }[] } | null;
  vendor?: { por_usuario: Record<string, number | null>; collectors: Record<string, string[]> } | null;
  waf?: { horas: number; requests: number; bloqueadas: number; polling_vendorweb: number;
          hosts: Record<string, number>; top: { metodo: string; uri: string; accion: string; n: number }[];
          headers: Record<string, string> } | null;
  alb?: { dias: number; por_dia: { dia: string; n: number }[]; dias_con_trafico: number; primer_dia: string | null;
          codigos: Record<string, number>; login_por_codigo: Record<string, number>; admin: number;
          top: { path_codigo: string; n: number }[] } | null;
  pares?: { ventana_n?: number; ips_con_volumen_parecido?: number; muestra?: string[]; ips_nuevas_en_historia?: number };
}
/** Lo que decidió el operador sobre un hit (queda en la misma entrada de bitácora). */
export interface VigiaAccionTomada {
  accion: "suprimir" | "vendor_conocido" | "ignorar"; por: string; ts: string; nota?: string;
  clave?: string; vence?: string; ip?: string;
}

export interface BitacoraEntry {
  ts:    string;
  /** Clave de la entrada en la tabla (las acciones del vigía la necesitan). */
  sk?:   string;
  tipo:  "investigacion" | "comentario" | "geo" | "alerta" | "ip" | "vigia";
  pais:  string | null;
  texto: string;
  por:   string;
  resultado?: {
    country: string; hours: number; muestras: number;
    rows: InvestigacionRow[];
    detalle_ip?: { ip: string; headers: Record<string, string> } | null;
    respuestas_resumen?: Record<string, number>;
    respuestas_error?: string | null;
    cidr?: string; action?: string; nota?: string;   // entradas tipo "ip"
    // entradas tipo "vigia"
    nivel?: VigiaNivel; veredicto?: VigiaVeredicto | null; hits?: VigiaHit[];
    ciegos?: string[]; ventana?: { desde: number; hasta: number };
    investigacion?: Record<string, VigiaInvestigacion>;
    accion_tomada?: VigiaAccionTomada;
  } | null;
}

/** Fase 3: el operador decide qué hacer con un hit del vigía. Nunca bloquea desde acá. */
export async function vigiaAccion(body: {
  sk: string; accion: "suprimir" | "vendor_conocido" | "ignorar";
  clave?: string; ip?: string; iid?: number | null; horas?: number; nota?: string;
}): Promise<BitacoraEntry> {
  const res = await authFetch(`${API_BASE}/metrics/vigia/accion`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res, "vigia-accion");
}

export interface VigiaEstado {
  supresiones: { sk: string; vence: string; nota?: string; por?: string; ts?: string }[];
  vendors: { sk: string; nota?: string; iid?: number; por?: string; ts?: string }[];
}
export async function getVigiaEstado(): Promise<VigiaEstado> {
  const res = await authFetch(`${API_BASE}/metrics/vigia/estado`, { headers: authHeader() });
  return jsonOrThrow(res, "vigia-estado");
}

export async function getBitacora(country?: string, tipo?: string): Promise<BitacoraEntry[]> {
  const p = new URLSearchParams();
  if (country) p.set("country", country);
  if (tipo) p.set("tipo", tipo);
  const qs = p.toString() ? `?${p}` : "";
  const res = await authFetch(`${API_BASE}/metrics/paises/bitacora${qs}`, { headers: authHeader() });
  return (await jsonOrThrow(res, "bitacora")).items as BitacoraEntry[];
}

export async function investigarPais(country: string, hours = 24): Promise<BitacoraEntry> {
  const res = await authFetch(`${API_BASE}/metrics/paises/investigar`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ country, hours }),
  });
  return jsonOrThrow(res, "investigar");
}

export async function addComentario(text: string, country?: string): Promise<BitacoraEntry> {
  const res = await authFetch(`${API_BASE}/metrics/paises/comentario`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ text, country: country || undefined }),
  });
  return jsonOrThrow(res, "comentario");
}

/** Bloquea/desbloquea un país a nivel WAF (regla webadmin2-geo-block). Solo admins. */
export async function geoToggle(country: string, action: "block" | "unblock"):
    Promise<{ blocked_countries: string[]; allowed_countries?: string[]; changed: boolean }> {
  const res = await authFetch(`${API_BASE}/metrics/paises/geo`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ country, action }),
  });
  return jsonOrThrow(res, "geo");
}

/** Habilita/deshabilita un país en la allowlist geo de infra (allow-traffic-only). Solo admins. */
export async function geoAllowToggle(country: string, action: "allow" | "disallow"):
    Promise<{ allowed_countries: string[]; blocked_countries: string[]; changed: boolean }> {
  const res = await authFetch(`${API_BASE}/metrics/paises/geo`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ country, action }),
  });
  return jsonOrThrow(res, "geo");
}

// ---------- Monitoreo de IPs (IP sets del WAF + logs) ----------
export type IpEstado = "habilitada" | "bloqueada" | "geo-no-habilitado" | "geo-bloqueado" | "normal";

export interface IpSetEntry {
  cidr:    string;
  ts?:     string;      // última acción en la bitácora
  por?:    string;
  texto?:  string;
  accion?: string;
}
export interface IpSets {
  allow: IpSetEntry[];
  block: IpSetEntry[];
  rules_installed: boolean;
  allowed_countries: string[];
  blocked_countries: string[];
  acl: string;
}
export interface IpTopRow {
  ip: string; country: string; total: number; blocked: number; uris: number;
  last: string | null; estado: IpEstado; cidr: string | null;
}
export interface IpTop {
  hours: number; from: string; to: string; total: number; blocked: number; ips: number;
  rows: IpTopRow[];
}
export interface IpBuscarRow {
  country: string | null; host: string | null; uri: string | null; action: string | null;
  rule: string | null; requests: number; first: string | null; last: string | null;
}
export interface IpBuscar {
  ip: string; hours: number; from: string; to: string; country: string | null;
  total: number; blocked: number; rows: IpBuscarRow[];
  headers: Record<string, string>; estado: IpEstado; cidr: string | null;
}

export async function getIpSets(): Promise<IpSets> {
  const res = await authFetch(`${API_BASE}/metrics/ips/sets`, { headers: authHeader() });
  return jsonOrThrow(res, "ips");
}
export async function getIpTop(hours: number): Promise<IpTop> {
  const res = await authFetch(`${API_BASE}/metrics/ips/top?hours=${hours}`, { headers: authHeader() });
  return jsonOrThrow(res, "ips-top");
}
export async function buscarIp(ip: string, hours: number): Promise<IpBuscar> {
  const res = await authFetch(`${API_BASE}/metrics/ips/buscar?ip=${encodeURIComponent(ip)}&hours=${hours}`,
    { headers: authHeader() });
  return jsonOrThrow(res, "ips-buscar");
}
export type IpAction = "allow" | "unallow" | "block" | "unblock";
/** Habilita/bloquea una IP o CIDR a nivel WAF. Solo admins. */
export async function ipToggle(ip: string, action: IpAction, nota?: string):
    Promise<{ cidr: string; action: IpAction; changed: boolean; allow: string[]; block: string[] }> {
  const res = await authFetch(`${API_BASE}/metrics/ips/toggle`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ ip, action, nota: nota || undefined }),
  });
  return jsonOrThrow(res, "ips-toggle");
}
